<!-- Checkbox combined with an editable text label, toggled by checking or by focusing the text. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import { vFocus } from '@nzyme/vue-utils/directives/vFocus.js';
import { computed } from 'vue';

import * as l from './CheckboxEditable.loc.js';
import type { CheckboxEditableValue } from './CheckboxEditable.ts';
import CheckboxTick from './CheckboxTick.vue';

defineProps({
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  focus: { type: Boolean, default: false },
  inputClass: classProp,
});

const t = useTranslate();

const model = defineModel<CheckboxEditableValue>({
  default: () => ({ checked: false, text: '' }),
});

const checked = computed({
  get: () => model.value.checked,
  set: value => {
    model.value = { ...model.value, checked: value };
  },
});

const text = computed({
  get: () => model.value.text,
  set: value => {
    model.value = { ...model.value, text: value };
  },
});

function toggleChecked() {
  if (!checked.value) {
    checked.value = true;
  }
}
</script>

<template>
  <label :class="[css.wrapper, disabled && css.disabled, readonly && css.readonly]">
    <div :class="css.checkbox">
      <button
        type="button"
        :class="[css.tick, readonly && css.readonly]"
        :disabled="disabled"
        @click="checked = !checked"
      >
        <CheckboxTick
          :checked="checked"
          :readonly="readonly"
          :disabled="disabled"
        />
      </button>

      <input
        v-model="text"
        v-focus="focus"
        type="text"
        :class="[css.input, inputClass]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :aria-label="placeholder || t(l.editableCheckboxText)"
        @focus="toggleChecked"
      />
    </div>
  </label>
</template>

<style lang="scss" module="css">
.wrapper {
  display: flex;
  flex-direction: column;
  user-select: none;

  &.readonly {
    cursor: default;
  }

  &.disabled {
    cursor: default;
  }
}

.checkbox {
  display: grid;
  grid-template-columns: auto 1fr;
  font-size: $font-size-14;
  align-items: flex-start;
}

.tick {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:not(.readonly):hover > * {
    border-color: $color-gray-700;
    background-color: $color-gray-50;
  }

  &:not(.readonly):focus-visible {
    @include input-focus-outline;
  }

  &:disabled {
    cursor: default;
  }
}

.input {
  flex: 1;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  outline: none;
  padding: 0;

  &::placeholder {
    color: $color-gray-600;
  }

  &:disabled {
    cursor: default;
    color: $color-gray-600;
    opacity: 0.5;
  }

  &:read-only {
    cursor: default;
  }

  .disabled & {
    filter: grayscale(1);
    color: $color-gray-600;
    opacity: 0.5;
  }
}
</style>
